import React, { ReactNode } from "react";

type Tone = "neutral" | "brand" | "success" | "warning" | "danger";

// Tinted fill + accessible text colour, the reference's `bg-brand/15` pattern
// re-tuned so every tone clears AA against a white surface.
const BADGE_TONES: Record<Tone, string> = {
  neutral: "bg-elevated text-zinc-600",
  brand: "bg-brand/10 text-brand-strong ring-1 ring-brand/20",
  success: "bg-emerald-500/10 text-emerald-700 ring-1 ring-emerald-500/20",
  warning: "bg-amber-500/10 text-amber-700 ring-1 ring-amber-500/20",
  danger: "bg-red-500/10 text-red-700 ring-1 ring-red-500/20",
};

export function Badge({
  tone = "neutral",
  className = "",
  title,
  children,
}: {
  tone?: Tone;
  className?: string;
  /** Native tooltip, e.g. to explain what a status marker means. */
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium ${BADGE_TONES[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

const ALERT_TONES: Record<Tone, string> = {
  neutral: "bg-elevated border-hairline text-zinc-700",
  brand: "bg-brand/5 border-brand/20 text-brand-strong",
  success: "bg-emerald-500/5 border-emerald-500/20 text-emerald-800",
  warning: "bg-amber-500/5 border-amber-500/20 text-amber-800",
  danger: "bg-red-500/5 border-red-500/20 text-red-800",
};

export function Alert({
  tone = "neutral",
  className = "",
  children,
}: {
  tone?: Tone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      role="alert"
      className={`rounded-xl border px-4 py-3 text-sm ${ALERT_TONES[tone]} ${className}`}
    >
      {children}
    </div>
  );
}

/** Indeterminate spinner. `size` is a Tailwind size utility, e.g. "size-5". */
export function Spinner({
  size = "size-5",
  className = "",
  label = "Loading",
}: {
  size?: string;
  className?: string;
  label?: string;
}) {
  return (
    <svg
      role="status"
      aria-label={label}
      viewBox="0 0 24 24"
      fill="none"
      className={`${size} animate-spin text-brand ${className}`}
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        className="opacity-20"
      />
      <path
        d="M22 12a10 10 0 0 0-10-10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
